import * as React from "react";

import { isNodeSelection, type Editor } from "@tiptap/react";

// --- Icons ---
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
// --- UI Primitives ---
import {
  UnifiedButton,
  UnifiedButtonProps,
} from "@/components/tiptap-ui/unified-button";
// --- Styles ---
import "@/components/tiptap-ui/link-popover/link-popover.scss";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { isMarkInSchema } from "@/lib/tiptap-utils";
import { faArrowTurnDownLeft } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Row } from "../../ui/flex";

const LinkButton = React.forwardRef<HTMLButtonElement, UnifiedButtonProps>(
  ({ className, children, ...props }: UnifiedButtonProps, ref) => {
    return (
      <UnifiedButton
        type="button"
        className={className}
        data-style="ghost"
        role="button"
        tabIndex={-1}
        aria-label="Link"
        tooltip="Link"
        ref={ref}
        {...props}
      >
        {children || (
          <FontAwesomeIcon icon={faLink} className="tiptap-button-icon" />
        )}
      </UnifiedButton>
    );
  },
);

interface LinkPopoverProps extends Omit<UnifiedButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * Whether to hide the link popover.
   * @default false
   */
  hideWhenUnavailable?: boolean;
  /**
   * Callback for when the popover opens or closes.
   */
  onOpenChange?: (isOpen: boolean) => void;
}

export function LinkPopover({
  editor: providedEditor,
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: LinkPopoverProps) {
  const editor = useTiptapEditor(providedEditor);
  const [isOpen, setIsOpen] = React.useState(false);
  const [url, setUrl] = React.useState("");

  const linkInSchema = isMarkInSchema("link", editor);

  const isDisabled = React.useMemo(() => {
    if (!editor) return true;
    if (editor.isActive("codeBlock")) return true;
    return !editor.can().setLink({ href: "" });
  }, [editor]);

  const canSetLink = React.useMemo(() => {
    if (!editor) return false;
    try {
      return editor.can().setMark("link");
    } catch {
      return false;
    }
  }, [editor]);

  const handleSetLink = () => {
    if (!url.trim() || !editor) return;

    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to);

    editor
      .chain()
      .focus()
      .insertContent({
        type: "text",
        text: text || url,
        marks: [{ type: "link", attrs: { href: url } }],
      })
      .run();

    setUrl("");
    setIsOpen(false);
  };

  const handleOnOpenChange = React.useCallback(
    (nextIsOpen: boolean) => {
      setIsOpen(nextIsOpen);
      onOpenChange?.(nextIsOpen);
      if (!nextIsOpen) {
        setUrl("");
      }
    },
    [onOpenChange],
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSetLink();
    }
  };

  const show = React.useMemo(() => {
    if (!linkInSchema) {
      return false;
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor?.state.selection) || !canSetLink) {
        return false;
      }
    }

    return true;
  }, [linkInSchema, hideWhenUnavailable, editor, canSetLink]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOnOpenChange}>
      <PopoverTrigger asChild>
        <LinkButton
          disabled={isDisabled}
          data-disabled={isDisabled}
          {...props}
        />
      </PopoverTrigger>

      <PopoverContent className="rounded-full p-1">
        <Row align="center">
          <input
            type="url"
            placeholder="Paste a link..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            className="clamp block h-10 w-full min-w-[50px] pl-2 outline-none"
            autoFocus
          />

          <div>
            <UnifiedButton
              type="button"
              onClick={handleSetLink}
              title="Create link"
              disabled={!url.trim()}
              data-style="ghost"
            >
              <FontAwesomeIcon icon={faArrowTurnDownLeft} />
            </UnifiedButton>
          </div>
        </Row>
      </PopoverContent>
    </Popover>
  );
}

LinkButton.displayName = "LinkButton";
